import { useEffect, useRef, useState } from 'react';
import { ReadingTracker } from '../tracking/tracker';
import { fetchBootstrap, documentUrl, type ViewerBootstrap } from '../api';
import { renderDocument } from '../pdf';

/**
 * The reading surface.
 *
 * Every page is a real DOM element with a data-page attribute, because the
 * tracker's IntersectionObserver measures which page is actually on screen.
 * A virtualised list or a canvas render would be faster and would destroy the
 * measurement — the observer needs elements that exist and have real geometry.
 */
export function DocumentViewer({ token }: { token: string }) {
  const [doc, setDoc] = useState<ViewerBootstrap | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The real PDF failed to load (e.g. asset uploaded before storage was
  // configured). We fall back to the height-accurate skeleton so tracking still
  // works and the reader sees the layout rather than a blank page.
  const [renderFailed, setRenderFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const trackerRef = useRef<ReadingTracker | null>(null);

  useEffect(() => {
    fetchBootstrap(token).then(setDoc).catch((e) => setError(e.message));
  }, [token]);

  // Paint the actual document once the page canvases are in the DOM.
  useEffect(() => {
    if (!doc) return;
    const ctrl = new AbortController();
    renderDocument(documentUrl(token), (n) => canvasRefs.current[n - 1], ctrl.signal)
      .catch(() => setRenderFailed(true));
    return () => ctrl.abort();
  }, [doc, token]);

  useEffect(() => {
    if (!doc || !containerRef.current) return;

    const tracker = new ReadingTracker({
      token,
      endpoint: import.meta.env.VITE_COLLECTOR_URL,
      pageCount: doc.pageCount,
    });
    // The container is a start() argument, not an option: the observer must
    // attach after the pages are in the DOM, which is only true by the time
    // this effect runs.
    tracker.start(containerRef.current);
    trackerRef.current = tracker;

    return () => tracker.stop();
  }, [doc]);

  if (error) {
    return (
      <main className="notice">
        <h1>Unable to open</h1>
        <p>{error}</p>
      </main>
    );
  }

  if (!doc) return <main className="notice"><p>Loading…</p></main>;

  return (
    <div className="viewer">
      <header className="viewer__bar">
        <h1 className="viewer__title">{doc.title}</h1>
        <div className="viewer__actions">
          <button onClick={() => trackerRef.current?.trackDownload()}>Download</button>
          <button onClick={() => trackerRef.current?.trackShare('link')}>Share</button>
        </div>
      </header>

      <div className="viewer__pages" ref={containerRef}>
        {Array.from({ length: doc.pageCount }, (_, i) => (
          <section key={i} className="page" data-page={i + 1}>
            <div className="page__num">{i + 1}</div>
            <div className="page__canvas-wrap">
              <canvas
                className="page__canvas"
                ref={(el) => { canvasRefs.current[i] = el; }}
              />
            </div>
            {renderFailed && <PageBody page={i + 1} words={doc.pageWordCounts[i] ?? 200} />}
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * Fallback body sized to the real word count of the page, shown only when the
 * actual PDF can't be loaded (e.g. an asset uploaded before storage was
 * configured). Pages of *different heights* matter: uniform pages make
 * scroll-depth and dwell-time behave linearly and every scoring bug hides.
 */
function PageBody({ page, words }: { page: number; words: number }) {
  const lines = Math.max(4, Math.round(words / 12));
  return (
    <div className="page__body">
      <h2>Section {page}</h2>
      {Array.from({ length: lines }, (_, i) => (
        <p key={i} className="page__line" style={{ width: `${70 + ((i * 37) % 30)}%` }} />
      ))}
    </div>
  );
}
